import React, { useState, useRef } from 'react';
import {
  View,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Theme } from '../../../utils/theme';
import CompactAudioRecorder from '../../../components/CompactAudioRecorder';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  style?: ViewStyle;
  textValue?: string; // optional controlled value
  onTextValueChange?: (value: string) => void; // optional controlled change
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  isStreaming = false,
  placeholder = "Ask your mentor anything...",
  style,
  textValue,
  onTextValueChange,
}) => {
  const isControlled = typeof textValue === 'string' && typeof onTextValueChange === 'function';
  const [message, setMessage] = useState('');
  const textInputRef = useRef<TextInput>(null);

  const currentValue = isControlled ? (textValue as string) : message;

  const handleSend = () => {
    const text = currentValue.trim();
    if (text && !disabled) {
      onSendMessage(text);
      if (isControlled) onTextValueChange && onTextValueChange('');
      else setMessage('');
      textInputRef.current?.blur();
    }
  };

  const handleVoiceTranscription = (transcription: string) => {
    const currentText = currentValue || '';
    const newText = currentText ? `${currentText} ${transcription}` : transcription;
    if (isControlled) onTextValueChange && onTextValueChange(newText);
    else setMessage(newText);
    
    // Focus text input after voice input
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 100);
  };

  const canSend = currentValue.trim().length > 0 && !disabled;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.inputContainer}>
        <TextInput
          ref={textInputRef}
          style={styles.textInput}
          placeholder={isStreaming ? "Mentor is responding..." : placeholder}
          placeholderTextColor={Theme.colors.textDisabled}
          value={currentValue}
          onChangeText={isControlled ? onTextValueChange : setMessage}
          multiline
          maxLength={1000}
          editable={!disabled && !isStreaming}
          returnKeyType="send"
          onSubmitEditing={handleSend}
          blurOnSubmit={false}
        />
        
        <View style={styles.actionButtons}>
          {/* Voice input button - HIDDEN FOR NOW */}
          {/* <View style={styles.voiceButton}>
            <CompactAudioRecorder
              onTranscription={handleVoiceTranscription}
              disabled={disabled || isStreaming}
            />
          </View> */}
          
          {/* Send button */}
          <Pressable
            style={[
              styles.sendButton,
              canSend ? styles.sendButtonActive : styles.sendButtonDisabled
            ]}
            onPress={handleSend}
            disabled={!canSend}
          >
            <Ionicons
              name="send"
              size={18}
              color={canSend ? Theme.colors.surface : Theme.colors.textDisabled}
            />
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.colors.surface,
    borderTopWidth: 0.5,
    borderTopColor: Theme.colors.border,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    paddingBottom: Platform.OS === 'ios' ? Theme.spacing.lg : Theme.spacing.md,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: Theme.spacing.sm,
    backgroundColor: Theme.colors.background,
    borderRadius: Theme.borderRadius.xl,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    minHeight: 44,
  },
  textInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    textAlignVertical: 'top',
    maxHeight: 150,
    paddingVertical: Theme.spacing.xs,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  voiceButton: {
    // CompactAudioRecorder handles its own styling
  },
  sendButton: {
    width: 32,
    height: 32,
    borderRadius: Theme.borderRadius.round,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonActive: {
    backgroundColor: Theme.colors.primary,
  },
  sendButtonDisabled: {
    backgroundColor: Theme.colors.disabled,
  },
});

export default ChatInput; 